import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LevelBadge from './LevelBadge';
import useSound from '../../hooks/useSound';

/**
 * Modal de level-up avec animation de célébration
 * Props:
 * - visible: boolean
 * - newLevel: number
 * - coinsEarned: number
 * - unlockedFeature: string | null (ex: "Mode Duel débloqué !")
 * - onClose: function
 */
function LevelUpModal({ visible, newLevel, coinsEarned = 0, unlockedFeature = null, onClose }) {
  const { playLevelUp } = useSound();

  // Fermeture auto après 4 secondes
  useEffect(() => {
    if (visible) {
      playLevelUp();
      const t = setTimeout(onClose, 4000);
      return () => clearTimeout(t);
    }
  }, [visible, onClose]);

  // Couleur du halo selon le niveau
  const getGlowColor = (lvl) => {
    if (lvl >= 50) return '#a855f7';
    if (lvl >= 30) return '#f0b429';
    if (lvl >= 20) return '#f97316';
    if (lvl >= 10) return '#3b82f6';
    if (lvl >= 5)  return '#22c55e';
    return '#94a3b8';
  };

  const glowColor = getGlowColor(newLevel);

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Overlay sombre */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.75)',
              zIndex: 10000,
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Modal centré */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10001,
              width: '85%',
              maxWidth: 340,
              background: 'linear-gradient(135deg, #1a4a2e 0%, #0d2b1a 100%)',
              border: `2px solid ${glowColor}`,
              borderRadius: 24,
              padding: '32px 24px',
              textAlign: 'center',
              boxShadow: `0 0 60px ${glowColor}55, 0 20px 60px rgba(0,0,0,0.8)`,
            }}
          >
            {/* Particules étoiles autour */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                  x: [0, Math.cos((i / 8) * Math.PI * 2) * 70],
                  y: [0, Math.sin((i / 8) * Math.PI * 2) * 70],
                }}
                transition={{ delay: 0.2 + i * 0.05, duration: 1.2 }}
                style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  marginTop: -6, marginLeft: -6,
                  color: glowColor,
                  fontSize: 12,
                  pointerEvents: 'none',
                }}
              >
                ★
              </motion.div>
            ))}

            {/* Titre LEVEL UP */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.3em',
                color: glowColor,
                textTransform: 'uppercase',
                marginBottom: 8,
                textShadow: `0 0 20px ${glowColor}`,
              }}
            >
              ⬆ LEVEL UP !
            </motion.div>

            {/* Badge niveau */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.15 }}
              style={{ marginBottom: 12 }}
            >
              <LevelBadge level={newLevel} size="lg" />
            </motion.div>

            {/* Texte niveau */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: '1.5rem',
                fontWeight: 900,
                color: '#fff',
                marginBottom: 16,
              }}
            >
              Niveau {newLevel} atteint !
            </motion.p>

            {/* Récompense pièces */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(240,180,41,0.15)',
                border: '1px solid rgba(240,180,41,0.4)',
                borderRadius: 50,
                padding: '8px 20px',
                marginBottom: unlockedFeature ? 12 : 20,
              }}
            >
              <span style={{ fontSize: 20 }}>⭐</span>
              <span style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 900,
                fontSize: '1.3rem',
                color: '#f0b429',
              }}>
                +{coinsEarned} pièces
              </span>
            </motion.div>

            {/* Fonctionnalité débloquée */}
            {unlockedFeature && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.55, type: 'spring' }}
                style={{
                  background: 'rgba(34,197,94,0.15)',
                  border: '1px solid rgba(34,197,94,0.4)',
                  borderRadius: 12,
                  padding: '10px 16px',
                  marginBottom: 20,
                  color: '#22c55e',
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.9rem',
                }}
              >
                🔓 {unlockedFeature}
              </motion.div>
            )}

            {/* Hint tap to close */}
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
              Appuie pour continuer
            </p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default LevelUpModal;
